import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, Eye, Loader2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useT } from "@/lib/LanguageContext";
// Relative on purpose: real modules under Jest, where there is no "@" alias.
import LoadFailure from "../../components/ui/LoadFailure";
import { PATHWAYS_ADMIN as IDS } from "../../constants/testIds/pathwaysAdmin";
import useTenantCurrency from "../../lib/useTenantCurrency";
import usePathwayBuilder from "../../pathways/usePathwayBuilder";
import { locateProblem, newStep, problemSubjects } from "../../pathways/builderModel";
import {
  activationProblemMessage,
  activationProblems,
  pathwayErrorCode,
  pathwayErrorMessage,
} from "../../pathways/errors";
import { useEntitlements } from "../entitlements/EntitlementsContext";
import { CAP } from "../entitlements/capabilities";
import CapabilityNotice from "../entitlements/CapabilityNotice";
import { adminHref } from "../adminPath";
import BasicsPanel, { BASICS_ANCHORS } from "./BasicsPanel";
import StepEditorModal from "./StepEditorModal";
import StructureCanvas, { levelAnchor, stepAnchor } from "./StructureCanvas";
import { levelLabel, statusLabel } from "./labels";
import { BUILDER_STEPS, pathwayPaths } from "./paths";
import { STATUS_PILL } from "./statusPill";

/**
 * `/admin/pathways/new` and `/admin/pathways/:pathwayId/edit?step=basics|structure`: the
 * pathway builder (spec screen 2, contract §9).
 *
 * Keyed by the pathway id, so a new pathway's first save (which moves the address to the
 * new id) remounts it on the stored copy, exactly as if it had been opened from the list.
 */
export default function PathwayBuilder() {
  const { pathwayId } = useParams();
  return <Builder key={pathwayId || "new"} pathwayId={pathwayId || null} />;
}

/**
 *   ┌ ← All pathways ─────────────────────────────────────────────────────────┐
 *   │ Prenatal Yoga Foundations  [Draft]      Preview as learner · Save draft · Activate │
 *   │ [1 · Basics] [2 · Structure]                                             │
 *   │ ⚠ Before this pathway can be activated:  Level 3 needs a mandatory step  │
 *   ├──────────────────────────────────────────────────────────────────────────┤
 *   │ Basics form  |  Structure canvas            (step editor opens as a dialog) │
 *   └──────────────────────────────────────────────────────────────────────────┘
 *
 * ONE DRAFT FOR BOTH STEPS. The step is a query parameter inside the same route, so this
 * component (and the draft in usePathwayBuilder) survives a switch: nothing is lost, and
 * Save writes everything in one full write.
 *
 * THE ACTIVATION PROBLEMS ARE THE SERVER'S (contract §4). The banner lists the problems of
 * the last refused write (a 422 PATHWAY_NOT_ACTIVATABLE: they describe what was sent),
 * else those of the stored pathway (`activation_problems`). Each one is a button that
 * opens the step of the builder it belongs to and scrolls to its level or step.
 *
 * PREVIEW renders the STORED pathway (contract §9.1), so unsaved edits are saved first.
 *
 * LEAVING WITH UNSAVED EDITS. "All pathways" asks first, and closing or reloading the tab
 * raises the browser's own prompt (`beforeunload`). Other console links are not
 * intercepted: the app runs on a `<BrowserRouter>`, and route blocking (`useBlocker`)
 * exists only under a data router.
 *
 * READ-ONLY without `learn.courses` (the server refuses authoring) and for an archived
 * pathway (every write is 409 PATHWAY_ARCHIVED).
 */
function Builder({ pathwayId }) {
  const t = useT("admin");
  const tc = useT("common");
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const builder = usePathwayBuilder(pathwayId);
  const tenantCurrency = useTenantCurrency();
  const ent = useEntitlements();
  const coursesState = ent.status(CAP.courses);
  const canAuthor = coursesState !== "not_in_plan" && coursesState !== "coming_soon";
  const { draft, pathway, errors, saveError, saving, activating, dirty, isNew } = builder;
  const archived = pathway?.status === "archived";
  const readOnly = !canAuthor || archived;
  const step = BUILDER_STEPS.includes(params.get("step")) ? params.get("step") : "basics";
  const [editing, setEditing] = useState(null);
  const [focusTarget, setFocusTarget] = useState(null);

  // The browser's own "leave site?" prompt while edits are unsaved.
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  // A problem was chosen in the banner: once its step of the builder is on screen (the tab
  // switch is a navigation, so it may land a render later), bring its row into view.
  useEffect(() => {
    if (!focusTarget || focusTarget.tab !== step) return;
    const el = document.getElementById(focusTarget.id);
    if (el) {
      if (typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "center", behavior: "smooth" });
      el.focus({ preventScroll: true });
    }
    setFocusTarget(null);
  }, [focusTarget, step]);

  // A save refused on the client highlights fields that may sit on the OTHER step of the
  // builder (a new Level pathway's untitled level is on Structure while the author is on
  // Basics). Each refusal replaces `errors`, so this runs once per attempt: it moves to the
  // step that holds the first highlighted field (Basics first, in document order) and
  // focuses that field once it is rendered.
  const [focusInvalid, setFocusInvalid] = useState(null);   // the step to focus on, or null
  useEffect(() => {
    const keys = Object.keys(errors || {});
    if (keys.length === 0) return;
    const onStructure = (k) => k === "levels" || k.startsWith("level:") || k.startsWith("step:");
    const target = keys.some((k) => !onStructure(k)) ? "basics" : "structure";
    if (target !== step) {
      setParams((p) => { const q = new URLSearchParams(p); q.set("step", target); return q; }, { replace: true });
    }
    setFocusInvalid(target);
    // `step` is read, not watched: a later tab change by the author must not re-trigger this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);
  useEffect(() => {
    if (!focusInvalid || focusInvalid !== step) return;
    const el = document.querySelector('[data-testid="' + IDS.builder + '"] [aria-invalid="true"]');
    if (el) {
      if (typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "center", behavior: "smooth" });
      el.focus({ preventScroll: true });
    }
    setFocusInvalid(null);
  }, [focusInvalid, step]);

  const refused = useMemo(() => activationProblems(saveError), [saveError]);
  const problems = useMemo(
    () => (refused.length > 0 ? refused : (pathway?.activation_problems || [])),
    [refused, pathway],
  );
  const problemKeys = useMemo(() => {
    const levels = new Set();
    const steps = new Set();
    if (draft) {
      problems.forEach((p) => {
        const at = locateProblem(draft, p);
        if (at.levelKey) levels.add(at.levelKey);
        if (at.stepKey) steps.add(at.stepKey);
      });
    }
    return { levels, steps };
  }, [draft, problems]);

  if (builder.loading && !draft) {
    return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-eh-muted" aria-label={t("path.loading")} /></div>;
  }
  if (builder.loadError || !draft) {
    const missing = pathwayErrorCode(builder.loadError) === "PATHWAY_NOT_FOUND" || builder.loadError?.response?.status === 404;
    return (
      <div data-testid={IDS.builder} className="space-y-4">
        <BackLink onClick={() => navigate(adminHref(pathwayPaths.list))} />
        <LoadFailure message={missing ? t("path.notFound") : tc("errors.loadFailed")} onRetry={builder.reload} retryLabel={t("path.retry")} />
      </div>
    );
  }

  const goStep = (next) => setParams((p) => { const q = new URLSearchParams(p); q.set("step", next); return q; }, { replace: true });

  const leave = () => {
    if (dirty && !window.confirm(t("path.confirmDiscard"))) return;
    navigate(adminHref(pathwayPaths.list));
  };

  /** After the first save of a new pathway, the address moves to its id (and remounts). */
  const settle = (saved) => {
    if (isNew && saved) navigate(adminHref(pathwayPaths.edit(saved.id, step)), { replace: true });
  };

  const onSave = async () => {
    const saved = await builder.save();
    if (!saved) return;
    toast.success(t("path.saved"));
    settle(saved);
  };

  const onActivate = async () => {
    const { pathway: latest, activated } = await builder.activate();
    if (activated) toast.success(t("path.activated"));
    else if (latest && isNew) {
      // The remount onto the new id shows the stored problems; say why it is still a draft.
      toast.error(t("path.activateRefused"));
    }
    settle(latest);
  };

  const onPreview = async () => {
    let target = pathway;
    if (!target || dirty) {
      target = await builder.save();
      if (!target) return;
    }
    navigate(adminHref(pathwayPaths.preview(target.id)));
  };

  const openProblem = (problem) => {
    const at = locateProblem(draft, problem);
    if (at.tab !== step) goStep(at.tab);
    let id = null;
    if (at.tab === "basics") id = problem.code === "OBJECTIVE_REQUIRED" ? BASICS_ANCHORS.objectives : BASICS_ANCHORS.dates;
    else if (at.stepKey) id = stepAnchor(at.stepKey);
    else if (at.levelKey) id = levelAnchor(at.levelKey);
    if (id) setFocusTarget({ tab: at.tab, id });
  };

  const saveStep = (levelKey, s) => {
    builder.upsertStep(levelKey, s);
    setEditing(null);
  };

  const hasLocalErrors = Object.keys(errors).length > 0;
  const busy = saving || activating;
  const published = draft.status === "published";
  const word = (p) => activationProblemMessage(p, tc, problemSubjects(draft, p, (n, title) => levelLabel(t, n, title)));

  return (
    <div data-testid={IDS.builder} className="space-y-5">
      <BackLink onClick={leave} />

      <header className="bg-white rounded-3xl border border-eh-border-soft p-5 sm:p-6 space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-start gap-4">
          <div className="flex-1 min-w-0">
            <h1 data-testid={IDS.builderTitle} className="font-display text-3xl text-eh-ink leading-tight break-words">
              {draft.title.trim() || t("path.builder.untitled")}
            </h1>
            <div className="flex flex-wrap items-center gap-2 mt-1.5">
              <span
                data-testid={IDS.builderStatus}
                data-status={draft.status}
                className={`text-[11px] px-2.5 py-0.5 rounded-full ${STATUS_PILL[draft.status] || STATUS_PILL.draft}`}
              >
                {statusLabel(t, draft.status)}
              </span>
              <span role="status" data-testid={IDS.builderUnsaved} className={`text-xs ${dirty ? "text-amber-800" : "text-eh-muted"}`}>
                {dirty ? t("path.unsaved") : isNew ? "" : t("path.allSaved")}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={onPreview} disabled={busy || (readOnly && !pathway)} data-testid={IDS.previewButton}>
              <Eye className="w-4 h-4 mr-2" aria-hidden="true" /> {t("path.builder.preview")}
            </Button>
            <Button
              variant="ghost"
              onClick={onSave}
              disabled={readOnly || busy || (!dirty && !isNew)}
              data-testid={IDS.saveDraft}
              className="text-eh-ochre-deep font-semibold"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />}
              {published ? t("path.builder.saveChanges") : t("path.builder.saveDraft")}
            </Button>
            {!published && !archived && (
              <Button onClick={onActivate} disabled={readOnly || busy} data-testid={IDS.activate} className="bg-eh-ink text-eh-cream hover:bg-eh-ink/90">
                {activating && <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />}
                {t("path.builder.activate")}
              </Button>
            )}
          </div>
        </div>

        <div role="tablist" aria-label={t("path.builder.steps")} className="flex gap-2">
          {BUILDER_STEPS.map((key, i) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={step === key}
              onClick={() => goStep(key)}
              data-testid={key === "basics" ? IDS.tabBasics : IDS.tabStructure}
              className={`rounded-full px-3 py-1 text-sm border ${
                step === key ? "bg-eh-ochre/15 border-eh-ochre-deep text-eh-ink font-semibold" : "bg-white border-eh-border-soft text-eh-muted hover:text-eh-ink"
              }`}
            >
              {t(`path.builder.tab.${key}`, { n: i + 1 })}
            </button>
          ))}
        </div>

        {problems.length > 0 && (
          <div data-testid={IDS.problems} role="region" aria-label={t("path.builder.problemsTitle")} className="rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3">
            <p className="flex items-center gap-2 text-sm font-medium text-amber-900">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" aria-hidden="true" /> {t("path.builder.problemsTitle")}
            </p>
            <ul className="mt-2 space-y-1">
              {problems.map((p, i) => (
                <li key={`${p.code}-${p.level_id || ""}-${p.step_id || ""}`}>
                  <button
                    type="button"
                    onClick={() => openProblem(p)}
                    data-testid={IDS.problem(i)}
                    className="text-left text-sm text-amber-900 underline decoration-amber-400 hover:decoration-amber-900"
                  >
                    {word(p)}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {((saveError && refused.length === 0) || hasLocalErrors) && (
          <div role="alert" data-testid={IDS.saveError} className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
            {saveError ? pathwayErrorMessage(saveError, tc, tc("errors.saveFailed")) : t("path.builder.fixHighlighted")}
          </div>
        )}
      </header>

      <CapabilityNotice state={coursesState} eta={ent.eta(CAP.courses)} body={t("path.planNotice")} testId={IDS.planNotice} />
      {archived && (
        <p data-testid={IDS.builderReadOnly} role="status" className="rounded-2xl border border-eh-border-soft bg-eh-cream/60 px-4 py-3 text-sm text-eh-ink flex items-center gap-2">
          <Lock className="w-4 h-4" aria-hidden="true" /> {t("path.builder.archivedReadOnly")}
        </p>
      )}

      <div role="tabpanel" aria-label={t(`path.builder.tab.${step}`, { n: BUILDER_STEPS.indexOf(step) + 1 })}>
        {step === "basics" ? (
          <BasicsPanel
            builder={builder}
            disabled={readOnly}
            typeLocked={Boolean(pathway?.path_type_locked)}
            currency={pathway?.currency || tenantCurrency}
          />
        ) : (
          <StructureCanvas
            builder={builder}
            disabled={readOnly}
            onOpenStep={(levelKey, s) => setEditing({ levelKey, step: s, isNew: false })}
            onAddStep={(levelKey) => setEditing({ levelKey, step: newStep(builder.nextKey()), isNew: true })}
            problemStepKeys={problemKeys.steps}
            problemLevelKeys={problemKeys.levels}
          />
        )}
      </div>

      <StepEditorModal
        editing={editing}
        onSave={saveStep}
        onClose={() => setEditing(null)}
        nextKey={builder.nextKey}
        schedule={draft.schedule}
        disabled={readOnly}
      />
    </div>
  );
}

function BackLink({ onClick }) {
  const t = useT("admin");
  return (
    <button type="button" onClick={onClick} data-testid={IDS.builderBack} className="inline-flex items-center gap-2 text-sm text-eh-muted hover:text-eh-ink">
      <ArrowLeft className="w-4 h-4" aria-hidden="true" /> {t("path.builder.back")}
    </button>
  );
}
